import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { useSiteMedia } from "../context/SiteMedia";
import { IMAGES } from "../assets/images";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";

export default function About() {
  const { t } = useLanguage();
  const { portraitUrl } = useSiteMedia();
  const a = t.aboutPage;

  return (
    <>
      <Seo title={a.seoTitle} description={a.seoDesc} />

      {/* INTRO */}
      <section className="pt-16 pb-20 md:py-28 border-b border-line-subtle relative overflow-hidden" data-testid="about-page-hero">
        <div className="absolute -left-24 -top-24 w-96 h-96 rounded-full bg-gold/5 blur-3xl pointer-events-none d1-drift" />
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-5 order-2 md:order-1">
            <figure className="relative aspect-[4/5] rounded-lg border border-line-strong overflow-hidden">
              <img
                src={portraitUrl || IMAGES.coachPortrait}
                alt={t.about.portraitCaption}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="about-page-portrait"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <figcaption className="absolute bottom-0 left-0 p-6 z-10 text-xs font-mono uppercase tracking-widest text-gold">
                {t.about.portraitCaption}
              </figcaption>
            </figure>
          </Reveal>

          <div className="md:col-span-7 order-1 md:order-2">
            <span className="text-xs font-mono uppercase tracking-widest text-gold">{a.eyebrow}</span>
            <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mt-2 mb-8" data-testid="about-page-title">
              <span className="ln"><span>{a.title}</span></span>
            </h1>
            <p className="text-lg text-bone leading-relaxed mb-6">{a.lead}</p>
            <p className="text-base text-bone-soft leading-relaxed mb-6">{a.body1}</p>
            <p className="text-base text-bone-soft leading-relaxed">{a.body2}</p>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="bg-coal-raise py-24 border-b border-line-subtle" data-testid="about-principles">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="max-w-2xl mb-16">
            <span className="text-xs font-mono uppercase tracking-widest text-gold">
              {a.principlesEyebrow}
            </span>
            <h2 className="font-display uppercase text-3xl sm:text-4xl tracking-tight text-white mt-2">
              {a.principlesTitle}
            </h2>
          </Reveal>

          <div className="grid sm:grid-cols-2 gap-6">
            {a.principles.map((p, i) => (
              <Reveal key={p.title} delay={i * 100}>
                <div
                  className="bg-coal-hover p-8 rounded-lg border border-line hover:border-gold/50 transition-colors h-full"
                  data-testid={`principle-card-${i + 1}`}
                >
                  <div className="text-xs font-mono uppercase text-gold mb-4">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-display text-2xl uppercase text-white mb-3">{p.title}</h3>
                  <p className="text-sm text-bone-soft leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display uppercase text-3xl sm:text-5xl tracking-tight text-white mb-4">
              {a.ctaTitle}
            </h2>
            <p className="text-sm text-bone-soft max-w-lg mx-auto mb-10">{a.ctaBody}</p>
            <Link
              to="/#intake"
              className="inline-flex justify-center items-center px-7 py-4 text-sm font-semibold tracking-wider uppercase bg-gold text-black rounded hover:bg-gold-bright transition-colors"
              data-testid="about-cta-button"
            >
              {a.ctaButton}
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
